import { authService } from '../services/authService';
import { resolveSafeBaseUrl } from '@platzona/auth-client';
import {
  OfflineQueuedError,
  bindBrowserConnectionEvents,
  clearOfflineOnLogout,
  entityKeyFromPath,
  enqueueMutation,
  isNetworkError,
  isOfflineQueueEnabled,
  isSaasCacheableGet,
  isSaasQueueableMutation,
  recallSaasGetCache,
  rememberSaasGetCache,
  shouldQueueHttpStatus,
  startSyncLoop,
} from '@platzona/offline';
import { clientPreviewMutatingHeaders, isMutatingHttpMethod } from './clientPreviewRequest';

const API_URL = resolveSafeBaseUrl(import.meta.env.VITE_API_URL || '');

/**
 * Thrown by apiFetch on a non-2xx response. Still a plain Error (message set,
 * `instanceof Error` holds), so every existing `catch (err) { err instanceof
 * Error ? err.message : ... }` call site keeps working unchanged. Callers that
 * need more than the message (e.g. a machine-readable `code`) can additionally
 * check `err instanceof ApiError ? err.data : undefined`.
 */
export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}
const OFFLINE_PLATFORM = 'saas';

let offlineBootstrapped = false;

function ensureOfflineSync(): void {
  if (offlineBootstrapped || typeof window === 'undefined') return;
  offlineBootstrapped = true;
  bindBrowserConnectionEvents();
  startSyncLoop({
    platform: OFFLINE_PLATFORM,
    execute: async (item) => {
      const makeRequest = async (token: string | null): Promise<Response> => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Idempotency-Key': item.idempotencyKey,
        };
        if (token) headers.Authorization = `Bearer ${token}`;
        if (isMutatingHttpMethod(item.method)) {
          Object.assign(headers, clientPreviewMutatingHeaders());
        }
        return fetch(`${API_URL}${item.path}`, {
          method: item.method,
          headers,
          body: item.body,
        });
      };
      let res = await makeRequest(authService.getToken());
      if (res.status === 401) {
        const newToken = await authService.silentRefresh();
        if (newToken) res = await makeRequest(newToken);
      }
      return res;
    },
  });

  window.addEventListener('platzona-auth-change', () => {
    if (!authService.getToken()) {
      void clearOfflineOnLogout(OFFLINE_PLATFORM);
    }
  });
}

async function queueOrThrow(
  method: string,
  path: string,
  body: string | null | undefined,
  cause: unknown,
): Promise<never> {
  if (!isOfflineQueueEnabled() || !isSaasQueueableMutation(method, path)) {
    throw cause instanceof Error ? cause : new Error(String(cause));
  }
  const item = await enqueueMutation({
    platform: OFFLINE_PLATFORM,
    method,
    path,
    body: body ?? null,
    entityKey: entityKeyFromPath(path),
  });
  throw new OfflineQueuedError(item.id);
}

/** Serve last-good GET JSON when the local backend is unreachable; else undefined. */
async function tryRecallSaasGetCache<T>(method: string, path: string): Promise<T | undefined> {
  if (method !== 'GET' || !isSaasCacheableGet(path)) return undefined;
  try {
    return await recallSaasGetCache<T>(path);
  } catch {
    return undefined;
  }
}

/**
 * Authenticated fetch helper with automatic 401 → token refresh → retry.
 * Allowlisted mutations enqueue to IndexedDB when the local backend is unreachable.
 * Allowlisted list/read GETs keep a last-good KV cache for offline browse.
 */
export async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  ensureOfflineSync();
  const method = (options.method || 'GET').toUpperCase();
  const body =
    typeof options.body === 'string'
      ? options.body
      : options.body != null
        ? String(options.body)
        : null;

  const makeRequest = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (isMutatingHttpMethod(options.method)) {
      Object.assign(headers, clientPreviewMutatingHeaders());
    }
    return fetch(`${API_URL}${path}`, {
      ...options,
      headers: { ...headers, ...(options.headers as Record<string, string> | undefined) },
    });
  };

  let res: Response;
  try {
    res = await makeRequest(authService.getToken());
  } catch (err) {
    if (isNetworkError(err)) {
      const cached = await tryRecallSaasGetCache<T>(method, path);
      if (cached !== undefined) return cached;
      await queueOrThrow(method, path, body, err);
    }
    throw err;
  }

  if (res.status === 401) {
    const newToken = await authService.silentRefresh();
    if (newToken) {
      try {
        res = await makeRequest(newToken);
      } catch (err) {
        if (isNetworkError(err)) {
          const cached = await tryRecallSaasGetCache<T>(method, path);
          if (cached !== undefined) return cached;
          await queueOrThrow(method, path, body, err);
        }
        throw err;
      }
    }
  }

  if (!res.ok && shouldQueueHttpStatus(res.status)) {
    const cached = await tryRecallSaasGetCache<T>(method, path);
    if (cached !== undefined) return cached;
    // A rate-limit (429) or gateway error (502/503) still usually carries a JSON body
    // with a human-readable `error` (e.g. "Too many requests. Please try again later."),
    // set by the backend's own rate-limit/error middleware. Prefer that over the bare
    // "HTTP 429" this used to throw unconditionally, which reached end users verbatim
    // (e.g. on the public customer-invite accept page) with no explanation or retry hint.
    let queueCause: Error = new Error(`HTTP ${res.status}`);
    try {
      const errorBody = (await res.json()) as { error?: string };
      if (errorBody && typeof errorBody.error === 'string' && errorBody.error) {
        queueCause = new ApiError(errorBody.error, res.status, errorBody);
      }
    } catch {
      /* not a JSON body (e.g. nginx's own 502 HTML page) — keep the generic message */
    }
    await queueOrThrow(method, path, body, queueCause);
  }

  const data = (await res.json()) as T;
  if (!res.ok) throw new ApiError((data as { error?: string }).error || 'Request failed', res.status, data);

  if (method === 'GET' && isSaasCacheableGet(path)) {
    void rememberSaasGetCache(path, data).catch(() => {
      /* best-effort; never break online success path */
    });
  }

  return data;
}

export { OfflineQueuedError, isOfflineQueuedError } from '@platzona/offline';
